"use client";

import { useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { ArrowDown, ArrowUp, CalendarDays, Loader2 } from "lucide-react";

export default function PublicationSort({ sort }: { sort: string }) {
  const router = useRouter();
  const params = useSearchParams();
  const [pending, startTransition] = useTransition();
  const oldest = sort === "oldest";

  function change(next: string) {
    if (next === sort || pending) return;
    const query = new URLSearchParams(params.toString());
    query.set("sort", next);
    startTransition(() => router.push(`/?${query.toString()}#latest-guides`, { scroll: false }));
  }

  return (
    <div className="mb-7 flex flex-col gap-3 rounded-2xl border border-border/60 bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between sm:px-4" aria-busy={pending}>
      <div className="flex items-center gap-2.5 px-1 text-sm font-medium text-muted-foreground">
        {pending ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <CalendarDays className="h-4 w-4" aria-hidden="true" />}
        <span>Sort by publication date</span>
      </div>
      <div role="group" aria-label="Sort by publication date" className="relative isolate grid w-full grid-cols-2 rounded-xl border border-border/60 bg-background p-1 shadow-sm sm:w-80">
        <span aria-hidden="true" className={`pointer-events-none absolute bottom-1 left-1 top-1 w-[calc(50%-4px)] rounded-lg bg-primary shadow-sm transition-transform duration-300 ease-out motion-reduce:transition-none ${oldest ? "translate-x-full" : "translate-x-0"}`} />
        {([{ value: "newest", label: "Newest first", Icon: ArrowDown }, { value: "oldest", label: "Oldest first", Icon: ArrowUp }]).map(({ value, label, Icon }) => (
          <button key={value} type="button" aria-label={label} title={label} aria-pressed={sort === value} disabled={pending} onClick={() => change(value)} className={`relative z-10 flex min-h-11 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary motion-reduce:transition-none disabled:cursor-wait ${sort === value ? "text-primary-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"}`}><Icon className="h-4 w-4 shrink-0" aria-hidden="true" /><span>{label}</span></button>
        ))}
      </div>
      <span role="status" className="sr-only">{pending ? "Sorting guides…" : `Guides sorted ${oldest ? "oldest" : "newest"} first`}</span>
    </div>
  );
}
